/**
 * Settings: the stages, edited as a draft while the live version keeps
 * running, with Try, Compare and Publish — and the name, schedule, doorbell
 * and delete.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useKeyed } from "@/components/integrations/hooks";
import { useMappingData } from "@/components/integrations/useMappingData";
import { FieldsStage } from "@/components/integrations/stages/FieldsStage";
import { MatchStage } from "@/components/integrations/stages/MatchStage";
import { RowsStage } from "@/components/integrations/stages/RowsStage";
import { ScheduleStage } from "@/components/integrations/stages/ScheduleStage";
import { SourceStage } from "@/components/integrations/stages/SourceStage";
import { TryStage } from "@/components/integrations/stages/TryStage";
import {
  Banner,
  Button,
  Card,
  ChangeList,
  Checkbox,
  Column,
  DangerConfirm,
  Disclosure,
  Field,
  FieldRow,
  Input,
  Section,
  Segmented,
  SkeletonRows,
  Stepper,
  TonePill,
  VersionBar,
  type StepperItem,
} from "@/components/kit";
import { ApiError } from "@/lib/api/client";
import { getAdminApiBaseUrl } from "@/lib/api/config";
import {
  clearScoutSchedule,
  connectDoorbell,
  deleteIntegration,
  getIntegration,
  getScoutSchedule,
  listIntegrations,
  publishDraft,
  saveDraft,
  setScoutSchedule,
  updateIntegration,
  type IntegrationDetail,
  type ScoutJob,
  type ScoutJobDetail,
} from "@/lib/integrations/api";
import { compareSettings } from "@/lib/integrations/compare";
import { cronOf, presetOf, scheduleWords, type Preset } from "@/lib/integrations/schedule";
import { normaliseSettings, type Settings } from "@/lib/integrations/settings";
import { ago, scoutJobStatus, when } from "@/lib/integrations/words";
import { errorText } from "@/spa/useApi";

type Stage = "source" | "rows" | "fields" | "match" | "try" | "schedule" | "doorbell";

type Said = { tone: "good" | "danger" | "warning" | "info"; text: string };

export function SettingsTab({
  item,
  canWrite,
  scoutJob,
  onChanged,
}: {
  item: IntegrationDetail;
  canWrite: boolean;
  scoutJob: ScoutJobDetail | undefined;
  onChanged: () => void;
}) {
  const navigate = useNavigate();
  const timeZone = item.eventTimezone ?? "UTC";
  const [start] = useState(() => normaliseSettings(item.draft?.settings ?? item.live?.settings ?? {}));
  const [settings, setSettings] = useState<Settings>(start);
  const [saved, setSaved] = useState(() => JSON.stringify(start));
  const [draftUpdatedAt, setDraftUpdatedAt] = useState<string | null>(item.draft?.updatedAt ?? null);
  const [stage, setStage] = useState<Stage>(item.draftVersion !== null ? "fields" : "fields");
  const [suggested, setSuggested] = useState<string[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [said, setSaid] = useState<Said | null>(null);
  const [conflict, setConflict] = useState<{ text: string; theirs: Settings; updatedAt: string | null } | null>(null);
  const [comparing, setComparing] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [note, setNote] = useState("");

  const mapping = stage === "rows" || stage === "fields" || stage === "match";
  const data = useMappingData(mapping ? item.code : null, settings);
  const siblings = useKeyed(publishing ? `siblings|${item.eventCode}` : null, () => listIntegrations(item.eventCode));
  const dirty = JSON.stringify(settings) !== saved;
  const live = normaliseSettings(item.live?.settings ?? {});
  const labels = Object.fromEntries((data.fields.data?.fields ?? []).map((field) => [field.key, field.label]));
  const missing = data.preview.data?.missing ?? [];
  const overlaps = (siblings.data ?? []).filter((one) => one.code !== item.code && one.importCommand === item.importCommand && one.state !== "off");
  const needsUpdate = item.needsUpdate ?? [];
  // Known only once the preview has loaded on a mapping stage.
  const missingKnown = data.preview.data ? missing : null;
  const now = (key: Stage): StepperItem["state"] | null => (stage === key ? "now" : null);
  const stages: StepperItem[] = [
    {
      key: "source",
      label: "Source",
      state: now("source") ?? (!item.scoutJobId ? "warn" : scoutJob?.lastPassed ? "done" : "todo"),
      note: !item.scoutJobId ? "No Scout job" : scoutJob ? scoutJobStatus(scoutJob.status).word : undefined,
    },
    { key: "rows", label: "Rows", state: now("rows") ?? "todo" },
    {
      key: "fields",
      label: "Fields",
      state: now("fields") ?? (needsUpdate.length > 0 || (missingKnown?.length ?? 0) > 0 ? "warn" : missingKnown ? "done" : "todo"),
      note:
        needsUpdate.length > 0
          ? `${needsUpdate.length} new to map`
          : missingKnown && missingKnown.length > 0
            ? `${missingKnown.length} need a value`
            : missingKnown
              ? "Needed fields mapped"
              : undefined,
    },
    { key: "match", label: "Match & safety", state: now("match") ?? "todo" },
    { key: "try", label: "Try", state: now("try") ?? "todo", note: dirty ? "Save first" : undefined },
    { key: "schedule", label: "Name & schedule", state: now("schedule") ?? "todo" },
    {
      key: "doorbell",
      label: "Doorbell & delete",
      state: now("doorbell") ?? (item.hasDoorbell ? "done" : "todo"),
      note: item.hasDoorbell ? "Connected" : "Not connected",
    },
  ];

  const save = async () => {
    setBusy("save");
    setSaid(null);
    try {
      const updated = await saveDraft(item.code, { settings, baseUpdatedAt: draftUpdatedAt });
      setSaved(JSON.stringify(settings));
      setDraftUpdatedAt(updated.draft?.updatedAt ?? null);
      setSaid({ tone: "good", text: `Draft ${updated.draftVersion} saved. The live version keeps running until you publish.` });
      onChanged();
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.status === 409) {
        const theirs = await getIntegration(item.code).catch(() => null);
        setConflict({
          text: thrown.message,
          theirs: normaliseSettings(theirs?.draft?.settings ?? {}),
          updatedAt: theirs?.draft?.updatedAt ?? null,
        });
      } else {
        setSaid({ tone: "danger", text: `${errorText(thrown, "omnium did not save the draft")} Your changes are kept on this page.` });
      }
    } finally {
      setBusy(null);
    }
  };

  const publish = () => {
    setBusy("publish");
    publishDraft(item.code, note.trim() || null)
      .then(
        (updated) => {
          setSaid({ tone: "good", text: `Version ${updated.liveVersion} is live. The version before it is kept under History.` });
          setPublishing(false);
          setNote("");
          setSaved(JSON.stringify(settings));
          setDraftUpdatedAt(null);
          onChanged();
        },
        (thrown) => setSaid({ tone: "danger", text: errorText(thrown, "omnium did not publish the draft") }),
      )
      .finally(() => setBusy(null));
  };

  return (
    <Column>
      <VersionBar
        live={item.liveVersion}
        draft={item.draftVersion}
        dirty={dirty}
        saving={busy}
        canWrite={canWrite}
        onSave={() => void save()}
        onTry={() => setStage("try")}
        onPublish={() => setPublishing(true)}
        onCompare={() => setComparing(!comparing)}
        publishProblem={missing.length ? `These fields must come from somewhere first: ${missing.join(", ")}` : null}
      />
      {said && <Banner tone={said.tone} actions={<Button tone="subtle" onClick={() => setSaid(null)}>Close</Button>}>{said.text}</Banner>}
      {conflict && (
        <Banner
          tone="warning"
          title="Someone else changed these settings."
          actions={
            <>
              <Button
                onClick={() => {
                  setSettings(conflict.theirs);
                  setSaved(JSON.stringify(conflict.theirs));
                  setDraftUpdatedAt(conflict.updatedAt);
                  setConflict(null);
                  onChanged();
                }}
              >
                Reload their settings
              </Button>
              <Button tone="subtle" onClick={() => setConflict(null)}>
                Keep mine on screen
              </Button>
            </>
          }
        >
          {conflict.text} Nothing was overwritten. Their draft differs from yours in:
          <ChangeList changes={compareSettings(settings, conflict.theirs, labels)} />
        </Banner>
      )}
      {comparing && (
        <Section title={`What this ${dirty ? "page" : "draft"} changes from live version ${item.liveVersion ?? "—"}`}>
          <Card>
            <ChangeList changes={compareSettings(live, settings, labels)} empty="The same as the live version." />
          </Card>
        </Section>
      )}
      {publishing && (
        <Banner
          tone="info"
          title={`Publish draft ${item.draftVersion}?`}
          actions={
            <>
              <Button tone="primary" disabled={busy !== null || !canWrite} onClick={publish}>
                {busy === "publish" ? "Publishing…" : "Publish"}
              </Button>
              <Button tone="subtle" disabled={busy !== null} onClick={() => setPublishing(false)}>
                Cancel
              </Button>
            </>
          }
        >
          It becomes the live version at once; the one before it is kept.
          <ChangeList changes={compareSettings(live, settings, labels)} empty="It has the same settings as the live version." />
          {overlaps.length > 0 && (
            <p style={{ margin: "var(--s2) 0 0", color: "var(--warn)" }}>
              {overlaps.map((one) => one.name).join(", ")} also {overlaps.length === 1 ? "writes" : "write"} through {item.importCommand} for this event. The newest run wins per field.
            </p>
          )}
          <div style={{ marginTop: "var(--s2)" }}>
            <Field label="Note for the history (optional)">
              <Input value={note} onChange={(event) => setNote(event.target.value)} placeholder="Status: added FINISHED" />
            </Field>
          </div>
        </Banner>
      )}

      <Stepper items={stages} label="Settings stages" onGo={(key) => setStage(key as Stage)} />

      {mapping && data.sample.error && (
        <Banner tone="warning" title="Scout is not answering." actions={<Button onClick={data.sample.reload}>Try again</Button>}>
          Settings can still be edited; previews wait for Scout. ({data.sample.error})
        </Banner>
      )}

      {stage === "source" && <SourcePart item={item} scoutJob={scoutJob} settings={settings} onSettings={setSettings} canWrite={canWrite} onChanged={onChanged} />}
      {stage === "rows" && (
        <RowsStage settings={settings} onChange={setSettings} sample={data.sample.data} preview={data.preview.data} previewBusy={data.preview.busy || data.sample.busy} disabled={!canWrite} />
      )}
      {stage === "fields" &&
        (data.fields.data ? (
          <FieldsStage
            code={item.code}
            settings={settings}
            onChange={setSettings}
            fields={data.fields.data}
            sample={data.sample.data}
            preview={data.preview.data}
            previewBusy={data.preview.busy}
            previewError={data.preview.error}
            run={data.run}
            timeZone={timeZone}
            suggested={suggested}
            onSuggested={setSuggested}
            disabled={!canWrite}
          />
        ) : data.fields.error ? (
          <Banner tone="danger" actions={<Button onClick={data.fields.reload}>Try again</Button>}>{data.fields.error}</Banner>
        ) : (
          <SkeletonRows rows={8} columns={5} label="Reading the import's fields" />
        ))}
      {stage === "match" && data.fields.data && (
        <MatchStage code={item.code} eventCode={item.eventCode} settings={settings} onChange={setSettings} fields={data.fields.data} disabled={!canWrite} />
      )}
      {stage === "try" && (
        <>
          {dirty && <Banner tone="warning">This page has changes that are not saved. Try runs a saved version: save the draft first to try your changes.</Banner>}
          <TryStage
            code={item.code}
            versions={[
              ...(item.draftVersion !== null ? [{ number: item.draftVersion, label: `Draft ${item.draftVersion}` }] : []),
              ...(item.liveVersion !== null ? [{ number: item.liveVersion, label: `Live ${item.liveVersion}` }] : []),
            ]}
            version={item.draftVersion ?? item.liveVersion}
            safety={settings.safety}
            timeZone={timeZone}
          />
        </>
      )}
      {stage === "schedule" && <SchedulePart item={item} scoutJob={scoutJob} canWrite={canWrite} onChanged={onChanged} />}
      {stage === "doorbell" && <DoorbellPart item={item} canWrite={canWrite} onChanged={onChanged} onDeleted={() => navigate("/integrations")} />}
    </Column>
  );
}

function SourcePart({
  item,
  scoutJob,
  settings,
  onSettings,
  canWrite,
  onChanged,
}: {
  item: IntegrationDetail;
  scoutJob: ScoutJobDetail | undefined;
  settings: Settings;
  onSettings: (settings: Settings) => void;
  canWrite: boolean;
  onChanged: () => void;
}) {
  const [base, setBase] = useState(item.scoutBaseUrl);
  const [swap, setSwap] = useState<ScoutJob | null>(null);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<Said | null>(null);
  const state = scoutJob ? scoutJobStatus(scoutJob.status) : null;
  return (
    <Column>
      <Card>
        <div style={{ display: "grid", gap: "var(--s2)" }}>
          <span>
            <strong>{scoutJob?.name ?? item.scoutJobId ?? "No Scout job yet"}</strong> {state && <TonePill tone={state.tone}>{state.word}</TonePill>}
          </span>
          <span style={{ color: "var(--ink-2)", fontSize: "var(--t-sm)", overflowWrap: "anywhere" }}>
            {scoutJob?.websiteUrl ?? ""} · Scout at {item.scoutBaseUrl}
          </span>
          {scoutJob?.lastPassed && (
            <span style={{ color: "var(--ink-2)", fontSize: "var(--t-sm)" }}>
              Newest passed run: {scoutJob.lastPassed.rows.toLocaleString()} rows, {ago(scoutJob.lastPassed.startedAt)}
            </span>
          )}
        </div>
      </Card>
      <div style={{ display: "grid", gap: "var(--s2)" }}>
        <strong>Which runs it reads</strong>
        <Segmented<"passed" | "omnium">
          name="Which runs"
          value={settings.source.runs}
          options={[
            { value: "passed", label: "Every passed run" },
            { value: "omnium", label: "Only runs omnium started" },
          ]}
          onChange={(runs) => onSettings({ ...settings, source: { ...settings.source, runs } })}
        />
        <span style={{ color: "var(--ink-3)", fontSize: "var(--t-sm)" }}>Part of the draft. Save and publish to use it.</span>
      </div>
      {said && <Banner tone={said.tone}>{said.text}</Banner>}
      {swap && (
        <Banner
          tone="warning"
          actions={
            <>
              <Button
                tone="primary"
                disabled={busy || !canWrite}
                onClick={() => {
                  setBusy(true);
                  updateIntegration(item.code, { scoutJobId: swap.id, scoutBaseUrl: base })
                    .then(
                      () => {
                        setSaid({ tone: "good", text: `Now reads ${swap.name}. Check the Fields stage for columns that no longer exist.` });
                        setSwap(null);
                        onChanged();
                      },
                      (thrown) => setSaid({ tone: "danger", text: errorText(thrown, "omnium did not swap the job") }),
                    )
                    .finally(() => setBusy(false));
                }}
              >
                {busy ? "Swapping…" : "Swap the job"}
              </Button>
              <Button tone="subtle" disabled={busy} onClick={() => setSwap(null)}>
                Cancel
              </Button>
            </>
          }
        >
          Read {swap.name} from now on? This takes effect at once, for the live version too. The mapping is kept; columns the new job does not send come back empty.
        </Banner>
      )}
      <Disclosure title="Change the Scout job" note="Swap to another job, or have Scout’s agent build one.">
        <SourceStage
          base={base}
          onBase={setBase}
          job={swap}
          onJob={(job) => job.id !== item.scoutJobId && setSwap(job)}
          imports={[]}
          command={item.importCommand}
          disabled={!canWrite}
        />
      </Disclosure>
    </Column>
  );
}

function SchedulePart({
  item,
  scoutJob,
  canWrite,
  onChanged,
}: {
  item: IntegrationDetail;
  scoutJob: ScoutJobDetail | undefined;
  canWrite: boolean;
  onChanged: () => void;
}) {
  const timeZone = item.eventTimezone ?? "UTC";
  const schedule = useKeyed(item.scoutJobId ? `schedule|${item.code}` : null, () => getScoutSchedule(item.code));
  const [preset, setPreset] = useState<Preset | null>(null);
  const shown = preset ?? presetOf(schedule.data?.timing ?? null);
  const [check, setCheck] = useState(item.checkSeconds);
  const [name, setName] = useState(item.name);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<Said | null>(null);
  const cron = preset ? cronOf(preset) : null;
  const changed = name.trim() !== item.name || check !== item.checkSeconds || preset !== null;

  const go = async () => {
    setBusy(true);
    const done: string[] = [];
    try {
      if (name.trim() !== item.name || check !== item.checkSeconds) {
        await updateIntegration(item.code, { name: name.trim(), checkSeconds: check });
        done.push("Name and check time saved.");
      }
      if (preset && cron) {
        await setScoutSchedule(item.code, cron, timeZone);
        done.push(`Scout now runs it: ${scheduleWords(preset, timeZone).toLowerCase()}.`);
      } else if (preset && preset.kind === "manual") {
        await clearScoutSchedule(item.code);
        done.push("Scout’s own schedule is off. It runs only when a person asks.");
      }
      setSaid({ tone: "good", text: done.join(" ") || "Nothing to save." });
      setPreset(null);
      schedule.reload();
      onChanged();
    } catch (thrown) {
      setSaid({ tone: "danger", text: `${errorText(thrown, "That did not save")} ${done.join(" ")}` });
    } finally {
      setBusy(false);
      setAsking(false);
    }
  };

  return (
    <Column>
      <FieldRow>
        <Field label="Name" hint="The code and the doorbell stay the same.">
          <Input value={name} disabled={!canWrite} onChange={(event) => setName(event.target.value)} />
        </Field>
      </FieldRow>
      {!item.scoutJobId ? (
        <Banner tone="info">Pick a Scout job first, on the Source stage.</Banner>
      ) : !schedule.data && !schedule.error ? (
        <SkeletonRows rows={2} columns={3} label="Reading Scout’s schedule" />
      ) : (
        <ScheduleStage
          preset={shown}
          onPreset={setPreset}
          checkSeconds={check}
          onCheckSeconds={setCheck}
          timeZone={timeZone}
          sharedWith={(scoutJob?.usedBy ?? []).filter((one) => one !== item.code)}
          scoutProblem={schedule.error}
        />
      )}
      {said && <Banner tone={said.tone}>{said.text}</Banner>}
      {asking ? (
        <Banner
          tone="warning"
          actions={
            <>
              <Button tone="primary" disabled={busy} onClick={() => void go()}>
                {busy ? "Saving…" : "Save"}
              </Button>
              <Button tone="subtle" disabled={busy} onClick={() => setAsking(false)}>
                Cancel
              </Button>
            </>
          }
        >
          {preset ? "This changes Scout’s schedule for the job at once. " : ""}Save the name and times?
        </Banner>
      ) : (
        <div>
          <Button tone="primary" disabled={!canWrite || !changed || !name.trim()} onClick={() => setAsking(true)}>
            Save name and schedule
          </Button>
        </div>
      )}
    </Column>
  );
}

function DoorbellPart({
  item,
  canWrite,
  onChanged,
  onDeleted,
}: {
  item: IntegrationDetail;
  canWrite: boolean;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const [address, setAddress] = useState(getAdminApiBaseUrl());
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [said, setSaid] = useState<Said | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [stopScout, setStopScout] = useState(false);

  const ring = () => {
    setBusy("bell");
    connectDoorbell(item.code, address)
      .then(
        (made) => {
          setSaid({ tone: "good", text: item.hasDoorbell ? `Secret rotated. Scout now rings ${made.url}.` : `Connected. Scout rings ${made.url} when a run passes.` });
          onChanged();
        },
        (thrown) => setSaid({ tone: "danger", text: errorText(thrown, "Scout did not make the webhook") }),
      )
      .finally(() => {
        setBusy(null);
        setAsking(false);
      });
  };

  const remove = async () => {
    setBusy("delete");
    try {
      if (stopScout && item.scoutJobId) await clearScoutSchedule(item.code);
      await deleteIntegration(item.code);
      onDeleted();
    } catch (thrown) {
      setSaid({ tone: "danger", text: errorText(thrown, "omnium did not delete it") });
      setBusy(null);
    }
  };

  return (
    <Column gap="lg">
      <Section title="The doorbell" note="Scout rings omnium when a run passes, signed with a secret omnium keeps and never shows.">
        <Card>
          <div style={{ display: "grid", gap: "var(--s3)" }}>
            <span>
              {item.hasDoorbell ? <TonePill tone="good">Connected</TonePill> : <TonePill tone="neutral">Not connected</TonePill>}{" "}
              {item.hasDoorbell ? "Runs come in the moment they pass." : `omnium checks Scout by itself after ${Math.round(item.checkSeconds / 60)} min of nothing.`}
            </span>
            <FieldRow>
              <Field label="The address Scout can reach omnium on">
                <Input value={address} disabled={!canWrite} onChange={(event) => setAddress(event.target.value.trim())} />
              </Field>
            </FieldRow>
            {said && <Banner tone={said.tone}>{said.text}</Banner>}
            {asking ? (
              <Banner
                tone="warning"
                actions={
                  <>
                    <Button tone="primary" disabled={busy !== null} onClick={ring}>
                      {busy === "bell" ? "Working…" : item.hasDoorbell ? "Rotate the secret" : "Connect"}
                    </Button>
                    <Button tone="subtle" disabled={busy !== null} onClick={() => setAsking(false)}>
                      Cancel
                    </Button>
                  </>
                }
              >
                {item.hasDoorbell
                  ? "Rotating makes a new Scout webhook and removes the old one. Runs that pass in between are found by omnium’s check."
                  : "This makes a webhook in Scout for this integration's job."}
              </Banner>
            ) : (
              <div>
                <Button disabled={!canWrite || !item.scoutJobId} onClick={() => setAsking(true)}>
                  {item.hasDoorbell ? "Rotate the secret" : "Connect the doorbell"}
                </Button>
              </div>
            )}
          </div>
        </Card>
      </Section>
      <Section title="Delete" note="Stops reading runs and removes Scout's webhook. Everything it already wrote stays.">
        {deleting ? (
          <DangerConfirm
            name={item.code}
            what={`Delete ${item.name}? Its runs, rows and versions go with it; the records it wrote into ${item.eventName} stay.`}
            action="Delete this integration"
            busy={busy === "delete"}
            onConfirm={() => void remove()}
            onCancel={() => setDeleting(false)}
          >
            <Checkbox label="Also stop Scout’s own schedule for this job" checked={stopScout} onChange={(event) => setStopScout(event.target.checked)} />
          </DangerConfirm>
        ) : (
          <div>
            <Button tone="danger" disabled={!canWrite} onClick={() => setDeleting(true)}>
              Delete…
            </Button>
          </div>
        )}
        <p style={{ margin: "var(--s3) 0 0", color: "var(--ink-3)", fontSize: "var(--t-sm)" }}>Last changed {when(item.draft?.updatedAt ?? item.live?.updatedAt ?? null, item.eventTimezone)}.</p>
      </Section>
    </Column>
  );
}
